import {FC, useEffect, useMemo, useState} from 'react'
import {toast} from 'react-toastify'
import {
  actOnApprovalInstance,
  getApprovalDocumentAvailableActions,
  getApprovalInstanceByDocument,
  getApprovalInstanceHistory,
  getPendingApprovalInstances,
  submitInvoiceForApproval,
  submitPurchaseOrderForApproval,
  submitRfqForApproval,
} from '../core/_requests'
import {
  ApprovalAvailableActions,
  ApprovalInstance,
  ApprovalInstanceActionType,
  ApprovalPolicyModuleType,
} from '../core/_models'

type ModuleKey = ApprovalPolicyModuleType | ''

const statusBadge = (status: string) => {
  const normalized = String(status || '').toUpperCase()
  if (normalized === 'APPROVED') return 'bg-success'
  if (normalized === 'REJECTED' || normalized === 'CANCELLED') return 'bg-danger'
  if (normalized === 'REWORK') return 'bg-warning text-dark'
  return 'bg-primary'
}

const ApprovalWorkflowPanel: FC = () => {
  const [moduleType, setModuleType] = useState<ModuleKey>('')
  const [documentId, setDocumentId] = useState('')
  const [pending, setPending] = useState<ApprovalInstance[]>([])
  const [history, setHistory] = useState<ApprovalInstance[]>([])
  const [selected, setSelected] = useState<ApprovalInstance | null>(null)
  const [availability, setAvailability] = useState<ApprovalAvailableActions | null>(null)
  const [remarks, setRemarks] = useState('')
  const [loading, setLoading] = useState(false)

  const moduleOptions = useMemo(() => ['RFQ', 'PURCHASE_ORDER', 'INVOICE'] as ApprovalPolicyModuleType[], [])

  const loadPending = async () => {
    setLoading(true)
    try {
      setPending(await getPendingApprovalInstances())
    } catch (error: any) {
      toast.error(error.message || 'Failed to load pending approvals')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    void loadPending()
  }, [])

  const loadDocument = async () => {
    if (!moduleType || !documentId.trim()) {
      toast.error('Select a module and enter a document id')
      return
    }
    await loadDocumentFor(moduleType, Number(documentId))
  }

  const loadDocumentFor = async (nextModuleType: ApprovalPolicyModuleType, docId: number) => {
    try {
      const current = await getApprovalInstanceByDocument(nextModuleType, docId)
      const rows = await getApprovalInstanceHistory(nextModuleType, docId)
      const actions = await getApprovalDocumentAvailableActions(nextModuleType, docId)
      setSelected(current)
      setHistory(rows)
      setAvailability(actions)
    } catch (error: any) {
      toast.error(error.message || 'Failed to load approval data')
    }
  }

  const submit = async () => {
    if (!moduleType || !documentId.trim()) {
      toast.error('Select a module and enter a document id')
      return
    }
    try {
      const docId = Number(documentId)
      if (moduleType === 'RFQ') {
        await submitRfqForApproval(docId)
      } else if (moduleType === 'PURCHASE_ORDER') {
        await submitPurchaseOrderForApproval(docId)
      } else {
        await submitInvoiceForApproval(docId)
      }
      await loadPending()
      await loadDocument()
      toast.success('Document submitted for approval')
    } catch (error: any) {
      toast.error(error.message || 'Failed to submit document for approval')
    }
  }

  const act = async (actionType: ApprovalInstanceActionType) => {
    if (!selected) return
    try {
      const updated = await actOnApprovalInstance(selected.id, actionType, remarks || undefined)
      setSelected(updated)
      setHistory(await getApprovalInstanceHistory(updated.moduleType, updated.documentId))
      const actions = await getApprovalDocumentAvailableActions(updated.moduleType, updated.documentId)
      setAvailability(actions)
      setRemarks('')
      await loadPending()
      toast.success(
        actionType === 'APPROVE'
          ? 'Approval approved'
          : actionType === 'REJECT'
            ? 'Approval rejected'
            : actionType === 'REWORK'
              ? 'Approval sent for rework'
              : 'Approval cancelled'
      )
    } catch (error: any) {
      toast.error(error.message || 'Failed to update approval')
    }
  }

  return (
    <div className='container-fluid py-4'>
      <div className='row g-4'>
        <div className='col-12 col-xl-4'>
          <div className='card shadow-sm h-100'>
            <div className='card-body'>
              <h3 className='h5 mb-3'>Approval Workflow</h3>
              <div className='mb-3'>
                <label className='form-label'>Module</label>
                <select className='form-select' value={moduleType} onChange={(e) => setModuleType(e.target.value as ModuleKey)}>
                  <option value=''>Select module</option>
                  {moduleOptions.map((opt) => (
                    <option key={opt} value={opt}>{opt}</option>
                  ))}
                </select>
              </div>
              <div className='mb-3'>
                <label className='form-label'>Document ID</label>
                <input className='form-control' value={documentId} onChange={(e) => setDocumentId(e.target.value)} />
              </div>
              <div className='d-flex gap-2 flex-wrap'>
                <button className='btn btn-primary' onClick={submit}>Submit</button>
                <button className='btn btn-outline-secondary' onClick={loadDocument}>Load</button>
              </div>

              <hr className='my-4' />

              <h4 className='h6 mb-3'>Current Instance</h4>
              {selected ? (
                <div className='small'>
                  <div><strong>Status:</strong> {selected.status}</div>
                  <div><strong>Document:</strong> {selected.moduleType} #{selected.documentId}</div>
                  <div><strong>Reference:</strong> {selected.documentReference || '-'}</div>
                  <div><strong>Current step:</strong> {selected.currentStepSequence ?? '-'}</div>
                  <div><strong>Submitted by:</strong> {selected.submittedByUsername || selected.submittedById}</div>
                </div>
              ) : (
                <div className='text-muted small'>No approval instance loaded.</div>
              )}

              <div className='mt-3'>
                {availability?.reason ? <div className='alert alert-warning py-2 small'>{availability.reason}</div> : null}
                <label className='form-label'>Remarks</label>
                <textarea className='form-control' rows={3} value={remarks} onChange={(e) => setRemarks(e.target.value)} />
              </div>
              <div className='d-flex gap-2 mt-3 flex-wrap'>
                <button className='btn btn-primary' disabled={!availability?.canSubmit} onClick={() => void submit()}>Submit</button>
                <button className='btn btn-success' disabled={!availability?.canApprove} onClick={() => void act('APPROVE')}>Approve</button>
                {availability?.canRework ? <button className='btn btn-warning' disabled={!selected} onClick={() => void act('REWORK')}>Rework</button> : null}
                <button className='btn btn-danger' disabled={!availability?.canReject} onClick={() => void act('REJECT')}>Reject</button>
                <button className='btn btn-outline-warning' disabled={!availability?.canCancel} onClick={() => void act('CANCEL')}>Cancel</button>
              </div>
            </div>
          </div>
        </div>

        <div className='col-12 col-xl-4'>
          <div className='card shadow-sm h-100'>
            <div className='card-body'>
              <h4 className='h6 mb-3'>Pending Queue</h4>
              {loading ? (
                <div className='text-muted'>Loading...</div>
              ) : pending.length === 0 ? (
                <div className='text-muted'>No pending approvals.</div>
              ) : (
                <div className='list-group'>
                  {pending.map((item) => (
                    <button
                      key={item.id}
                      className='list-group-item list-group-item-action text-start'
                      onClick={() => {
                        setSelected(item)
                        setModuleType(item.moduleType)
                        setDocumentId(String(item.documentId))
                        void loadDocumentFor(item.moduleType, item.documentId)
                      }}
                    >
                      <div className='d-flex justify-content-between align-items-center'>
                        <strong>{item.moduleType} #{item.documentId}</strong>
                        <span className={`badge ${statusBadge(item.status)} text-white`}>{item.status}</span>
                      </div>
                      <div className='small text-muted'>{item.policyName || 'Approval policy'}</div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        <div className='col-12 col-xl-4'>
          <div className='card shadow-sm h-100'>
            <div className='card-body'>
              <h4 className='h6 mb-3'>History</h4>
              {history.length === 0 ? (
                <div className='text-muted'>No history loaded.</div>
              ) : (
                <div className='vstack gap-3'>
                  {history.map((row) => (
                    <div key={row.id} className='border rounded p-3'>
                      <div className='d-flex justify-content-between align-items-center'>
                        <strong>{row.status}</strong>
                        <span className='text-muted small'>{row.submittedAt || ''}</span>
                      </div>
                      <div className='small text-muted'>Step {row.currentStepSequence ?? '-'}</div>
                      <div className='small'>Submitted by {row.submittedByUsername || row.submittedById}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default ApprovalWorkflowPanel
